import React, { FC, createContext, useContext } from "react"
import { Table } from "antd"
import { HolderOutlined } from "@ant-design/icons"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core"
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import type { ColumnsType } from "antd/es/table"

const DragListenersContext = createContext<Record<string, any> | undefined>(undefined)

interface BodyRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  "data-row-key"?: string
}

interface DraggableRowProps extends BodyRowProps {
  "data-row-key": string
}

const isDraggableRowProps = (props: BodyRowProps): props is DraggableRowProps => props["data-row-key"] !== undefined

const DraggableRow: FC<DraggableRowProps> = ({ children, ...props }) => {
  const id = props["data-row-key"]
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const yOnlyTransform = transform ? { ...transform, x: 0 } : null
  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(yOnlyTransform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999, background: "#fafafa" } : {}),
  }

  return (
    <DragListenersContext.Provider value={listeners}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes}>
        {children}
      </tr>
    </DragListenersContext.Provider>
  )
}

const BodyRow: FC<BodyRowProps> = props => isDraggableRowProps(props) ? <DraggableRow {...props} /> : <tr {...props} />

export const DragHandle: FC = () => {
  const listeners = useContext(DragListenersContext)

  return (
    <HolderOutlined
      style={{ cursor: "grab", color: "#999", marginRight: 8 }}
      {...listeners}
    />
  )
}

interface DraggableTableProps<T> {
  dataSource: T[] | undefined
  columns: ColumnsType<T>
  rowKey: (record: T) => string
  onDragEnd: (activeId: string, overId: string, activeIndex: number, overIndex: number) => void
  onRow?: (record: T) => React.HTMLAttributes<HTMLTableRowElement>
  loading?: boolean
  scroll?: { x?: string | number | true; y?: string | number }
}

export function DraggableTable<T extends { id: string }>({
  dataSource,
  columns,
  rowKey,
  onDragEnd,
  onRow,
  loading,
  scroll,
}: DraggableTableProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 1 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id || !dataSource) return

    const activeIndex = dataSource.findIndex(item => rowKey(item) === active.id)
    const overIndex = dataSource.findIndex(item => rowKey(item) === over.id)

    if (activeIndex !== -1 && overIndex !== -1) {
      onDragEnd(active.id as string, over.id as string, activeIndex, overIndex)
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={dataSource?.map(rowKey) || []}
        strategy={verticalListSortingStrategy}
      >
        <Table<T>
          dataSource={dataSource}
          columns={columns}
          rowKey={rowKey}
          pagination={false}
          scroll={scroll}
          loading={loading}
          components={{ body: { row: BodyRow } }}
          onRow={onRow}
        />
      </SortableContext>
    </DndContext>
  )
}

export function reorderPriorities<T extends { id: string; priority: number }>(
  items: T[],
  activeIndex: number,
  overIndex: number,
  updateFn: (item: T) => Promise<T>,
  updateEntryFn: (item: T) => void,
): Promise<void> {
  const updates: T[] = []

  const ensureSmallImageUrl = (item: T): T => {
    if ("smallImageUrl" in item && "imageUrl" in item) {
      const advertiserItem = item as T & { smallImageUrl?: string; imageUrl?: string }
      if (!advertiserItem.smallImageUrl && advertiserItem.imageUrl) {
        return { ...item, smallImageUrl: advertiserItem.imageUrl } as T
      }
    }

    return item
  }

  if (activeIndex < overIndex) {
    for (let i = activeIndex + 1; i <= overIndex; i++) {
      const item = ensureSmallImageUrl({ ...items[i], priority: items[i].priority - 1 })
      updates.push(item)
    }
    const activeItem = ensureSmallImageUrl({ ...items[activeIndex], priority: items[overIndex].priority })
    updates.push(activeItem)
  } else {
    for (let i = overIndex; i < activeIndex; i++) {
      const item = ensureSmallImageUrl({ ...items[i], priority: items[i].priority + 1 })
      updates.push(item)
    }
    const activeItem = ensureSmallImageUrl({ ...items[activeIndex], priority: items[overIndex].priority })
    updates.push(activeItem)
  }

  return Promise.all(updates.map(updateFn)).then(updated => {
    updated.forEach(updateEntryFn)
  })
}
